"use client";

import { Capacitor, registerPlugin } from "@capacitor/core";
import { createClientLogger } from "@/lib/logging/client";

const logger = createClientLogger("lib/native/hiive-push-permissions");

/**
 * The five states iOS actually has, plus the two Android can express.
 *
 * `provisional` is the one Capacitor's own plugin cannot report: it folds
 * `.ephemeral`, `.authorized` and `.provisional` into a single `granted`, which
 * would make a quietly-authorized user look identical to a fully authorized one.
 * The whole push-nudge system targets the former, so the distinction is the
 * reason this bridge exists.
 */
export type NativePushAuthorizationStatus =
  | "notDetermined"
  | "denied"
  | "provisional"
  | "authorized"
  | "ephemeral"
  | "unknown";

export type NativePushPermissionStatus = {
  platform: "ios" | "android";
  status: NativePushAuthorizationStatus;
  /** iOS only. Android has no quiet-trial equivalent. */
  provisionalSupported: boolean;
  /**
   * Whether a system dialog could still appear. The nudge kit must never offer
   * a primary CTA when this is false — the call would resolve to nothing and
   * the tap would read as broken.
   */
  canRequestFull: boolean;
  /** Android: true only in the "denied once, the dialog will show again" window. */
  shouldShowRationale: boolean;
};

type HiivePushPermissionsPlugin = {
  getStatus(): Promise<NativePushPermissionStatus>;
  requestProvisional(): Promise<NativePushPermissionStatus>;
  requestFull(): Promise<NativePushPermissionStatus>;
  markRequested(): Promise<NativePushPermissionStatus>;
  clearChatNotifications(input: { chatId: string }): Promise<void>;
};

export async function clearIosChatNotifications(chatId: string) {
  await HiivePushPermissions.clearChatNotifications({ chatId });
}

const HiivePushPermissions = registerPlugin<HiivePushPermissionsPlugin>(
  "HiivePushPermissions",
);

/**
 * What to assume when the native plugin is unreachable — an older binary that
 * predates it, or the web build.
 *
 * Deliberately pessimistic about `canRequestFull`: a false negative renders one
 * fewer nudge, while a false positive ships a button that does nothing.
 */
const unavailable = (): NativePushPermissionStatus => ({
  platform: (Capacitor.getPlatform() === "android"
    ? "android"
    : "ios") as NativePushPermissionStatus["platform"],
  status: "unknown",
  provisionalSupported: false,
  canRequestFull: false,
  shouldShowRationale: false,
});

const guard = async (
  operation: Exclude<
    keyof HiivePushPermissionsPlugin,
    "clearChatNotifications"
  >,
): Promise<NativePushPermissionStatus> => {
  if (!Capacitor.isNativePlatform()) return unavailable();

  try {
    return await HiivePushPermissions[operation]();
  } catch (error) {
    // A binary shipped before this plugin existed answers "not implemented".
    // JS ships without a rebuild (see project_native_js_ships_without_rebuild),
    // so this is the expected state for every install until the next release —
    // never an exception worth surfacing.
    logger.warn(`push permission bridge unavailable (${operation})`, error);
    return unavailable();
  }
};

export const getNativePushStatus = () => guard("getStatus");

/**
 * Grants quietly on iOS when nothing has been decided. No dialog is shown, and
 * the one-shot alert budget is left intact for {@link requestNativeFullPush}.
 *
 * A no-op on Android, which has no provisional equivalent.
 */
export const requestNativeProvisionalPush = () => guard("requestProvisional");

/** iOS only: the single call in the app that can raise the system alert. */
export const requestNativeFullPush = () => guard("requestFull");

/**
 * Android only: records that the app is about to show the system dialog, so a
 * later read can tell "never asked" from "asked twice and permanently blocked".
 * Both otherwise report `shouldShowRationale === false`.
 */
export const markNativePushRequested = () => guard("markRequested");
